// Pure helpers for the landing dashboard: words and widths only. Who is in flight, who is next, every count and
// every fraction's inputs come from the API (ADR 0003 §4); this file turns them into sentences and bar widths.
import { STAGE_WORDS } from '../pages/Deals/stages'
import type { DealStage } from '../types/deals'
import { formatDate } from './format'
import { ageWords } from './pipeline'
import type { DashboardOpportunity, DashboardStage, DashboardWeek } from '../types/dashboard'

/** The greeting for the local hour: morning until noon, afternoon until six, evening after. */
export function greeting(hour: number): string {
  if (hour < 12) return 'Good morning'
  if (hour < 18) return 'Good afternoon'
  return 'Good evening'
}

/** The first name out of a display name: "Don Agee" → "Don"; "Agee, Don" → "Don"; an email gives the part before @. */
export function firstName(displayName: string | null | undefined): string {
  if (!displayName) return ''
  const name = displayName.replace(/[(@].*$/, '').trim()
  const comma = name.indexOf(',')
  const given = comma >= 0 ? name.slice(comma + 1) : name
  return given.trim().split(/\s+/)[0] ?? ''
}

/** The stage in the pipeline's own words; an unknown stage is shown as it came. */
export function stageWords(stage: string): string {
  return STAGE_WORDS[stage as DealStage] ?? stage
}

/** Where a stage opens: leads have their own list; every other stage lives on Opportunities. */
export function stageRoute(stage: string): string {
  return stage === 'lead' ? '/leads' : '/opportunities'
}

/** The muted line under an in-flight card: the API's clause, then the counts that are on it. */
export function activatedWords(o: Pick<DashboardOpportunity, 'activated' | 'correspondences' | 'plays'>): string {
  const parts = [capitalize(o.activated.trim())]
  if (o.correspondences > 0) parts.push(count(o.correspondences, 'message', 'messages') + ' on it')
  if (o.plays > 0) parts.push(count(o.plays, 'play', 'plays') + ' planned')
  return parts.filter(Boolean).join(' · ')
}

/** The week in one or two plain sentences: what went out and what came back, then who wrote and how many rooms. */
export function weekSentences(week: DashboardWeek): string[] {
  const out: string[] = []
  if (week.sent === 0 && week.drafted === 0) {
    out.push('Nothing left your outbox this week.')
  } else {
    let s = week.sent === 0 ? `You drafted ${count(week.drafted, 'message', 'messages')} and sent none` : `You sent ${count(week.sent, 'message', 'messages')}`
    if (week.sent > 0 && week.drafted > 0) s += ` and drafted ${week.drafted} more`
    if (week.sent > 0) s += week.replied === 0 ? '; none has drawn a reply yet' : `; ${week.replied} ${week.replied === 1 ? 'has' : 'have'} drawn a reply`
    out.push(s + '.')
  }
  const wrote = week.wroteToYou === 0 ? 'Nobody wrote to you' : `${count(week.wroteToYou, 'person', 'people')} wrote to you`
  const met = week.meetings === 0 ? 'you had no meetings' : `you had ${count(week.meetings, 'meeting', 'meetings')}`
  out.push(`${wrote}, and ${met}.`)
  return out
}

export interface PipelineBar extends DashboardStage {
  /** the bar's width as a fraction of the fullest stage by count, 0..1 */
  countFraction: number
  /** the same by amount */
  amountFraction: number
}

/** Bars by stage, in the order the API gave them, widths as fractions of the largest. All zero is all empty. */
export function pipelineBars(stages: DashboardStage[]): PipelineBar[] {
  const maxCount = Math.max(0, ...stages.map((s) => s.count))
  const maxAmount = Math.max(0, ...stages.map((s) => s.amount))
  return stages.map((s) => ({
    ...s,
    countFraction: maxCount > 0 ? Math.max(0, Math.min(1, s.count / maxCount)) : 0,
    amountFraction: maxAmount > 0 ? Math.max(0, Math.min(1, s.amount / maxAmount)) : 0,
  }))
}

/** A fraction 0..1 as a CSS width. */
export function widthOf(fraction: number): string {
  return `${Math.round(Math.max(0, Math.min(1, fraction)) * 100)}%`
}

/** The reason kind in words — the same table the API labels its own counts with (OutreachReasons.Label). */
export function reasonWords(kind: string): string {
  switch (kind) {
    case 'they-wrote-unanswered': return 'They wrote, you have not'
    case 'next-step-owed': return 'You owe the next step'
    case 'warm-gone-quiet': return 'Cadence has lapsed'
    case 'meeting-follow-up': return 'Follow up the meeting'
    case 'new-lead': return 'New lead'
    case 'overdue-reply-from-them': return 'Waiting on them'
    case 'return-to-due': return 'They said come back'
    case 'referral-ask': return 'Ask for a referral'
    case 'new-investment': return 'News at their account'
    case 'season-greeting': return "Season's greeting"
    default: return kind.replace(/-/g, ' ')
  }
}

/** Days since the last contact, as a partner says it; null when nothing is on file. */
export function daysWords(days: number | null): string {
  if (days == null) return 'no contact on file'
  if (days <= 0) return 'in touch today'
  if (days === 1) return 'in touch yesterday'
  return `${ageWords(days)} since contact`
}

/** The tint a 0..1 tie strength wears on the strength bar. Display only. */
export function strengthClass(strength: number): string {
  if (strength >= 0.66) return 'strong'
  if (strength >= 0.33) return 'warm'
  return 'faint'
}

/** The workbench link for an account, with the person and the opportunity it should open on when known. */
export function craftLink(companyId: string, personId?: string | null, engagementId?: string | null): string {
  const params = new URLSearchParams()
  if (personId) params.set('person', personId)
  if (engagementId) params.set('opportunity', engagementId)
  const qs = params.toString()
  return `/workbench/${encodeURIComponent(companyId)}${qs ? `?${qs}` : ''}`
}

/**
 * A day the API named ("2026-09-25", a next step's date or the last activity) as "Sep 25, 2026" in every time
 * zone: a date-only string is read as local midnight, never as UTC midnight the night before. An instant with a
 * time part goes through formatDate as it is.
 */
export function dayWords(iso: string): string {
  return formatDate(/^\d{4}-\d{2}-\d{2}$/.test(iso) ? `${iso}T00:00:00` : iso)
}

/** "3 people", "1 message". */
export function count(n: number, one: string, many: string): string {
  return `${n} ${n === 1 ? one : many}`
}

function capitalize(s: string): string {
  return s.length === 0 ? s : s[0].toUpperCase() + s.slice(1)
}
